import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import DataTable, { Column } from "../components/DataTable";
import AdminDataModal, { FormField } from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useFormat } from "../../context/FormatContext";
import "../styles/AdminTestimonials.css";

type ServiceStatus = "active" | "inactive";

interface ServiceRow {
  id: number;
  title: string;
  description: string;
  icon: string;
  link: string;
  sort_order: number;
  status: ServiceStatus;
  createdAt: string;
  updatedAt: string;
}

type ModalMode = "add" | "edit" | "view";

const API_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api";

const defaultRowData = {
  id: "",
  title: "",
  description: "",
  link: "",
  icon: "",
  sort_order: 0,
  status: "active",
};

const AdminServices: React.FC = () => {
  const { showModal } = useMessageModal();
  const { formatDateToDMY } = useFormat();

  const [rows, setRows] = useState<ServiceRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<ServiceRow | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("add");
  const [modalRowData, setModalRowData] = useState<any>(defaultRowData);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/services`, {
        withCredentials: true,
      });

      setRows(response.data?.data || []);
    } catch (error) {
      console.error("fetchServices error:", error);
      showModal({
        type: "error",
        title: "Error",
        message: "Unable to fetch services.",
      });
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleAdd = () => {
    setModalMode("add");
    setModalRowData(defaultRowData);
    setIsModalOpen(true);
  };

  const handleView = (row: ServiceRow) => {
    setModalMode("view");
    setModalRowData(row);
    setIsModalOpen(true);
  };

  const handleEdit = (row: ServiceRow) => {
    setModalMode("edit");
    setModalRowData(row);
    setIsModalOpen(true);
  };

  const handleDelete = async (row: ServiceRow) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${row.title}"?`,
    );
    if (!confirmed) return;

    try {
      await axios.delete(`${API_URL}/admin/services/${row.id}`, {
        withCredentials: true,
      });

      showModal({
        type: "success",
        title: "Success",
        message: "Service deleted successfully.",
      });

      setSelectedRow(null);
      fetchServices();
    } catch (error: any) {
      showModal({
        type: "error",
        title: "Error",
        message: error?.response?.data?.message || "Unable to delete service.",
      });
    }
  };

  const handleSubmit = async (formData: any) => {
    if (!formData.title?.trim() || !formData.description?.trim()) {
      showModal({
        type: "confirm",
        title: "Validation",
        message: "Title and description are required.",
      });
      return;
    }

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        link: formData.link,
        icon: formData.icon,
        sort_order: formData.sort_order || 0,
        status: formData.status || "active",
      };

      if (modalMode === "add") {
        await axios.post(`${API_URL}/admin/services`, payload, {
          withCredentials: true,
        });
      } else {
        await axios.put(`${API_URL}/admin/services/${formData.id}`, payload, {
          withCredentials: true,
        });
      }

      showModal({
        type: "success",
        title: "Success",
        message: `Service ${
          modalMode === "add" ? "added" : "updated"
        } successfully.`,
      });

      setIsModalOpen(false);
      setSelectedRow(null);
      setModalRowData(defaultRowData);
      fetchServices();
    } catch (error: any) {
      showModal({
        type: "error",
        title: "Error",
        message: error?.response?.data?.message || "Unable to save service.",
      });
    }
  };

  const adminServices: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID" },
      { key: "title", label: "Title", type: "text" },
      { key: "icon", label: "FontAwesome Icon (fa-...)", type: "text" },
      {
        key: "sort_order",
        label: "Sort Order",
        type: "number",
      },
      { key: "link", label: "Service Link", colSpan: 2 },
      {
        key: "description",
        label: "Description",
        type: "textarea",
        colSpan: 1,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
      },
    ],
    [],
  );

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "title", label: "Title" },
    {
      key: "icon",
      label: "Icon",
      render: (row: ServiceRow) => <i className={`fa ${row.icon} fa-lg`} />,
    },
    {
      key: "description",
      label: "Description",
      render: (row: ServiceRow) => (
        <span className="admin-testimonial-text-clamp">{row.description}</span>
      ),
    },
    {
      key: "link",
      label: "Link",
      render: (row: ServiceRow) => (
        <span className="admin-testimonial-text-clamp">{row.link}</span>
      ),
    },
    { key: "sort_order", label: "Sort Order" },
    {
      key: "status",
      label: "Status",
      render: (row: ServiceRow) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Created",
      render: (row: ServiceRow) =>
        row.createdAt ? formatDateToDMY(row.createdAt) : "-",
    },
  ];

  return (
    <div className="admin-page-wrap">
      <AdminListToolkit
        title="Services"
        columns={columns}
        data={rows}
        selectedRow={selectedRow}
        onAdd={handleAdd}
        onView={handleView}
        onEdit={handleEdit}
        onSoftDelete={handleDelete}
        enableExport
        exportFileName="services"
      >
        {(filteredData: any) => {
          const dataArray = Array.isArray(filteredData)
            ? filteredData
            : filteredData?.data || [];

          return (
            <DataTable
              columns={columns}
              data={dataArray}
              selectedRowId={selectedRow?.id ?? null}
              onRowSelect={(row: ServiceRow) => setSelectedRow(row)}
            />
          );
        }}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={isModalOpen}
        mode={modalMode}
        title="Service"
        fields={adminServices}
        rowData={modalRowData}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        onFormChange={setModalRowData}
      />
    </div>
  );
};

export default AdminServices;
